import { memo } from 'react';
import { useIntl } from 'react-intl';
import { HStack, VStack } from '@library';
import ErrorBoundary from '../../core/components/ErrorBoundary';
import useStockLocations from '../hooks/useStockLocations';
import useLoginState from '../../core/hooks/useLoginState';
import useProductOrderRestriction from '../queries/useProductOrderRestriction';
import getBranchName from '../util/getBranchName';
import GeneralStockIndicatorWithData from './GeneralStockIndicatorWithData';
import StockIndicatorWithData from './StockIndicatorWithData';

interface ProductStockIndicatorProps {
    skuList?: string[];
    sku?: string;
    desiredQuantity?: number;
    messageStyle?: 'left' | 'right' | 'tooltip' | 'none';
    lineIndicator?: boolean;
    vertical?: boolean;
    location?: string;
    hideBranchesWithoutAvailability?: boolean;
    skipLoadingStocks?: boolean;
    hideSecondaryBranchWhileLoading?: boolean;
    quantityToIgnore?: number;
}

const ProductStockIndicatorComponent = memo(
    ({
        skuList,
        sku,
        desiredQuantity = 1,
        messageStyle = 'tooltip',
        lineIndicator = false,
        vertical = false,
        location,
        hideBranchesWithoutAvailability = false,
        skipLoadingStocks,
        hideSecondaryBranchWhileLoading = false,
        quantityToIgnore = 0,
    }: ProductStockIndicatorProps) => {
        const intl = useIntl();
        const isLoggedIn = useLoginState();
        const { branches, shouldShowBranchCode } = useStockLocations(location);
        const { restriction } = useProductOrderRestriction({
            sku,
            skuList,
            skip: skipLoadingStocks,
        });

        if (!isLoggedIn || (!sku && !skuList)) {
            return null;
        }

        const LayoutComponent = vertical ? VStack : HStack;
        const branchCountToTake = lineIndicator || restriction ? 1 : undefined;

        const isPersistMessage =
            messageStyle !== 'tooltip' && messageStyle !== 'none';

        return (
            <LayoutComponent
                wrap={isPersistMessage}
                gap={isPersistMessage ? 'small' : 'micro'}
                align={vertical ? 'start' : undefined}
            >
                {branches
                    .slice(0, branchCountToTake)
                    .map(branch =>
                        sku ? (
                            <StockIndicatorWithData
                                key={branch.id}
                                lineIndicator={lineIndicator}
                                sku={sku}
                                branchName={getBranchName(branch, intl)}
                                location={branch.id}
                                desiredQuantity={desiredQuantity}
                                messageStyle={messageStyle}
                                hideBranchesWithoutAvailability={
                                    hideBranchesWithoutAvailability
                                }
                                hideBranchCode={
                                    !shouldShowBranchCode ||
                                    !!restriction?.restrictionId
                                }
                                skipLoadingStocks={skipLoadingStocks}
                                isSecondaryBranch={
                                    hideSecondaryBranchWhileLoading &&
                                    !branch.isMainStock
                                }
                                quantityToIgnore={quantityToIgnore}
                            />
                        ) : (
                            <GeneralStockIndicatorWithData
                                key={branch.id}
                                skuList={skuList!}
                                branchName={getBranchName(branch, intl)}
                                location={branch.id}
                                messageStyle={messageStyle}
                                hideBranchesWithoutAvailability={
                                    hideBranchesWithoutAvailability
                                }
                                hideBranchCode={
                                    !shouldShowBranchCode ||
                                    !!restriction?.restrictionId
                                }
                                isSecondaryBranch={
                                    hideSecondaryBranchWhileLoading &&
                                    !branch.isMainStock
                                }
                            />
                        )
                    )}
            </LayoutComponent>
        );
    }
);

const ProductStockIndicator = (props: ProductStockIndicatorProps) => {
    return (
        <ErrorBoundary customMessage="Failed to load availability">
            <ProductStockIndicatorComponent {...props} />
        </ErrorBoundary>
    );
};

export default memo(ProductStockIndicator);
